/**
 * Shared email helpers.
 *
 * Two things are exported here:
 *  - `normalizeEmailInput` — the transformation applied on every keystroke, so that
 *    upper case letters are replaced with their lower case counterparts as the user types.
 *  - the basic validation primitives (`EMAIL_REGEXP`, `isValidEmail`, `getEmailErrorKey`)
 *    used by every email field across the app.
 *
 * Keep the rules in this single place so all email fields behave identically.
 */

/** RFC 5321 limit for the whole address. */
export const EMAIL_MAX_LENGTH = 254;

/**
 * Basic email validation:
 *  - a non-empty local part (letters, digits and the common `!#$%&'*+/=?^_`{|}~-` symbols),
 *    optionally split by single dots,
 *  - a single `@`,
 *  - a domain made of one or more dot-separated labels that start and end with
 *    a letter or a digit and may contain hyphens,
 *  - a top level domain of at least two letters.
 *
 * The pattern is intentionally lower case only: values are normalized before being tested.
 */
export const EMAIL_REGEXP =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/;

/** Translation keys returned by `getEmailErrorKey`. */
export const EMAIL_ERROR_KEYS = {
  required: 'messages.fieldRequired',
  invalid: 'messages.emailNotValid',
} as const;

export type EmailErrorKey = (typeof EMAIL_ERROR_KEYS)[keyof typeof EMAIL_ERROR_KEYS];

/**
 * Normalizes what the user types into an email field:
 * upper case letters become lower case and any whitespace is dropped
 * (spaces are never valid inside an address, and they are the most common paste artifact).
 */
export const normalizeEmailInput = (value?: string | null): string =>
  (value ?? '').replace(/\s+/g, '').toLowerCase();

/** True when the field holds no address at all. */
export const isEmailEmpty = (value?: string | null): boolean =>
  normalizeEmailInput(value).length === 0;

/** True when the value is a syntactically valid address. Empty values are NOT valid. */
export const isValidEmail = (value?: string | null): boolean => {
  const email = normalizeEmailInput(value);
  return email.length > 0 && email.length <= EMAIL_MAX_LENGTH && EMAIL_REGEXP.test(email);
};

/**
 * Returns the translation key of the error for the given value, or `null` when the value is fine.
 * An empty value is only an error when the field is required.
 */
export const getEmailErrorKey = (value?: string | null, required = false): EmailErrorKey | null => {
  if (isEmailEmpty(value)) {
    return required ? EMAIL_ERROR_KEYS.required : null;
  }
  return isValidEmail(value) ? null : EMAIL_ERROR_KEYS.invalid;
};
